import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";

// Searchable multi-select. options: [{value,label}] ; value: string[]
export const MultiPicker = ({ label, options, value = [], onChange, testId, width = "w-72", maxItems = 300 }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = new Set(value);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? options.filter((o) => o.value.toLowerCase().includes(needle) || (o.label || "").toLowerCase().includes(needle)) : options;
    const chosen = list.filter((o) => sel.has(o.value));
    const rest = list.filter((o) => !sel.has(o.value));
    return [...chosen, ...rest].slice(0, maxItems);
  }, [q, options, value]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (v) => onChange(sel.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const summary = value.length === 0 ? `All ${label.toLowerCase()}` : value.length === 1 ? (options.find((o) => o.value === value[0])?.label || value[0]) : `${value.length} selected`;
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId} className={`h-9 ${width} justify-between font-normal ${value.length ? "border-sky-400 bg-sky-50/60 text-sky-900 pr-12" : "text-slate-600"}`}>
            <span className="truncate text-xs">{summary}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        {value.length > 0 && (
          <button type="button" aria-label="Clear" data-testid={`${testId}-clear`} onClick={() => onChange([])} className="absolute right-7 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-red-600 hover:bg-white">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <PopoverContent className={`${width} p-0`} align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} value={q} onValueChange={setQ} data-testid={`${testId}-search`} />
            <CommandList className="max-h-72">
              <CommandEmpty>No match</CommandEmpty>
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)} data-testid={`${testId}-opt-${o.value}`} className="cursor-pointer">
                    <span className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${sel.has(o.value) ? "bg-sky-700 border-sky-700 text-white" : "border-slate-300"}`}>{sel.has(o.value) && <Check className="h-3 w-3" />}</span>
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm">{o.label || o.value}</span>{o.label && <span className="block truncate font-mono text-[10px] text-slate-500">{o.value}</span>}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <div className="flex items-center justify-between border-t px-2 py-1.5 text-[11px] text-slate-500">
              <span>{options.length.toLocaleString("en-IN")} options{filtered.length === maxItems ? ` · showing first ${maxItems}` : ""}</span>
              <span className="flex gap-2"><button type="button" className="hover:text-slate-900" onClick={() => onChange([])}>Clear</button><button type="button" className="text-sky-700 font-semibold" onClick={() => setOpen(false)}>Done</button></span>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
      </div>
    </div>
  );
};
